import { prioritizeAndMapDropdownItems } from '@approvalmax/ui';
import { selectors } from 'modules/common';
import { du } from 'modules/data';
import { AsyncDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getRequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { getRuleTypeByEditMode } from 'pages/requestForm/utils/helpers';
import { FC, memo, PropsWithChildren, useCallback } from 'react';
import { api } from 'services/api';
import { isOcrEnabledForTemplate } from 'shared/helpers';

import { useGetRecognizedOcrAttachment } from '../../../../hooks/useGetRecognizedOcrAttachment';

const NetSuiteExpenseCategoriesDataProvider: FC<PropsWithChildren> = (props) => {
    const { children } = props;

    const request = useSelector(requestSelector);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const ruleType = getRuleTypeByEditMode(editMode);

    const { id: requestId, companyId } = request;

    const company = useSelector((state) => selectors.company.getCompanyById(state, companyId));

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const shouldUseOcr = ocrAttachment && isOcrEnabledForTemplate(company, request.integrationCode);

    const { data: ocrData, refetch: refetchOcrData } = useOcrData(request, ocrAttachment?.id);

    const onLoad = useCallback(
        async (filterText: string | null, startFrom: number, count: number) => {
            const response = await api.integration.getNetSuiteExpenseCategories({
                requestId,
                ruleType,
                query: filterText,
                startFrom,
                rowNum: count,
                companyId,
            });

            const parsedCategories = response.Values.map((item) => ({
                ...du.parseServerReference(item),
                accountId: item.AccountId || null,
                rates: (item.Rates || []).map((rate) => ({
                    currency: rate.CurrencyInternalId,
                    defaultRate: rate.DefaultRate,
                })),
            }));

            if (shouldUseOcr) {
                const data = ocrData || (await refetchOcrData()).data;

                return prioritizeAndMapDropdownItems(parsedCategories, data?.document?.vendorIds);
            }

            return parsedCategories;
        },
        [requestId, companyId, shouldUseOcr, ocrData, ruleType, refetchOcrData]
    );

    return (
        <AsyncDataProvider
            cacheStorageId={`requestOptions.netSuiteExpenseCategories.${requestId}`}
            pageSize={100}
            onLoad={onLoad}
        >
            {children}
        </AsyncDataProvider>
    );
};

export default memo(NetSuiteExpenseCategoriesDataProvider);
